import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IFolder } from '@apis/documents';
import type { INewFolder } from '../hooks/useNewFolder';
import { buildTree } from '../utils/buildTree';
import { FolderNavContext } from './FolderNav.context';
import { TreeNode } from './TreeNode';
import { ContextMenu } from '../ContextMenu/ContextMenu';
import { canCreateSubfolder, siblingNamesOf, validateFolderName } from '../utils/validation';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { folderDisplayName } from '../utils/folderDisplayName';
import { nameErrorMessage } from '../utils/validationMessages';
import styles from './FolderNav.module.css';

interface ICtxMenu {
  x: number;
  y: number;
  nodeId: string;
}

interface IFolderNavProps {
  folders: IFolder[];
  selectedId: string;
  isPending?: boolean;
  isUnavailable?: boolean;
  onSelect: (id: string) => void;
  /** Absent when the user may not write documents: rename is not offered. */
  onRenameFolder?: (id: string, newName: string) => void;
  onDeleteFolder: (id: string) => void;
  canDelete?: boolean;
  newFolder: INewFolder | null;
  /** Absent when the user may not write documents: no new folder is offered. */
  onStartNewFolder?: (parentId: string) => boolean;
  onCommitNewFolder: (name: string) => void;
  onCancelNewFolder: () => void;
  invalidDropId?: string | null;
}

const SKELETON_ROWS: { width: string; indent: number }[] = [
  { width: '72%', indent: 0 },
  { width: '55%', indent: 1 },
  { width: '60%', indent: 1 },
  { width: '80%', indent: 0 },
  { width: '45%', indent: 1 },
];

export function FolderNav({
  folders, selectedId, isPending = false, isUnavailable = false, onSelect,
  onRenameFolder, onDeleteFolder, canDelete = false, newFolder, onStartNewFolder, onCommitNewFolder, onCancelNewFolder,
  invalidDropId = null,
}: IFolderNavProps) {
  const { t } = useTranslation();
  // Root folders carry Genba's Japanese rootName — relabel via the shared
  // helper for the tree only; the original `folders` stays untouched for
  // rename/lookup.
  const tree = useMemo(() => {
    const labelled = folders.map((f) => ({ ...f, name: folderDisplayName(t, f) }));
    return buildTree(labelled, t('documents.allDocuments'));
  }, [folders, t]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set(['all']));
  const [ctxMenu, setCtxMenu] = useState<ICtxMenu | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const originalName = useRef('');
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderError, setNewFolderError] = useState<string | null>(null);

  // Selection can come from outside the tree (folder rows in the file table,
  // a deep-linked URL) — expand the selected folder and its ancestor chain so
  // the highlighted node is actually visible in the sidebar. Render-time state
  // adjustment (not an effect) per React's derived-state pattern.
  //
  // Keyed on the selection but held back until the tree has loaded: a deep link
  // arrives before `folders` does, and there are no ancestors to walk yet. Once
  // loaded it runs for that selection and not again, so a folder the user
  // collapses by hand stays collapsed through unrelated tree changes.
  const [revealedFor, setRevealedFor] = useState<string | null>(null);
  const revealFor = folders.length > 0 ? selectedId : null;
  if (revealFor !== null && revealFor !== revealedFor) {
    setRevealedFor(revealFor);
    if (revealFor !== 'all') {
      setExpanded((prev) => {
        const next = new Set(prev);
        let id: string | null = revealFor;
        while (id) {
          next.add(id);
          id = folders.find((f) => f.id === id)?.parentId ?? null;
        }
        return next.size === prev.size ? prev : next;
      });
    }
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const nextExpanded = new Set(prev);
      if (nextExpanded.has(id)) nextExpanded.delete(id);
      else nextExpanded.add(id);
      return nextExpanded;
    });
  }

  function openNodeMenu(e: React.MouseEvent, nodeId: string) {
    e.preventDefault();
    e.stopPropagation();
    // The virtual "all" root has no actions — nothing can be created, renamed, or
    // deleted at the root level.
    if (nodeId === 'all') { setCtxMenu(null); return; }
    setCtxMenu({ x: e.clientX, y: e.clientY, nodeId });
  }

  function openBackgroundMenu(e: React.MouseEvent) {
    // Suppress the browser menu but offer nothing: folders can only be created
    // inside a root, never as a sibling of the server-owned roots.
    e.preventDefault();
    setCtxMenu(null);
  }

  function startRename(nodeId: string) {
    const folder = folders.find((f) => f.id === nodeId);
    const name = folder?.name ?? '';
    originalName.current = name;
    setRenameValue(name);
    setRenameError(null);
    setRenamingId(nodeId);
    if (folder?.parentId) {
      setExpanded((prev) => new Set([...prev, folder.parentId!]));
    }
  }

  // Re-validate as the user types so the error clears the moment the name is
  // valid again, instead of waiting for the next commit attempt.
  function changeRename(value: string) {
    setRenameValue(value);
    if (renameError) setRenameError(value.trim() ? renameErrorOf(value) : null);
  }

  /** The names a folder under `parentId` must not repeat — itself excluded. */
  function siblingNames(parentId: string | null, exceptId: string | null): string[] {
    return siblingNamesOf(folders.filter((f) => f.parentId === parentId), exceptId);
  }

  function nameErrorOf(value: string, siblings: readonly string[]): string | null {
    const err = validateFolderName(value, siblings);
    return err ? nameErrorMessage(t, err) : null;
  }

  // Only called mid-rename, so the folder being renamed is in the list.
  function renameErrorOf(value: string): string | null {
    const renamed = folders.find((f) => f.id === renamingId)!;
    return nameErrorOf(value, siblingNames(renamed.parentId, renamed.id));
  }

  // Only called while a new folder is being named.
  function newFolderErrorOf(value: string): string | null {
    return nameErrorOf(value, siblingNames(newFolder!.parentId, null));
  }

  function commitRename() {
    if (!renamingId) return;
    const trimmed = renameValue.trim();
    // An empty value (or a blurred new folder left untouched) just cancels;
    // a non-empty but invalid name keeps the editor open with an error.
    if (!trimmed) { cancelRename(); return; }
    // An untouched name is a cancel, not a rename request (which would refetch
    // the tree and drop every cached file for nothing).
    if (trimmed === folders.find((f) => f.id === renamingId)!.name) { cancelRename(); return; }
    const message = renameErrorOf(trimmed);
    if (message) { setRenameError(message); return; }
    onRenameFolder?.(renamingId, trimmed);
    setRenamingId(null);
    setRenameError(null);
  }

  function cancelRename() {
    setRenamingId(null);
    setRenameError(null);
  }

  function handleNewSubfolder(parentId: string) {
    if (!onStartNewFolder?.(parentId)) return;
    setExpanded((prev) => new Set([...prev, parentId]));
    setNewFolderName(t('documents.contextMenu.newFolder'));
    setNewFolderError(null);
    setRenamingId(null);
  }

  function changeNewFolder(value: string) {
    setNewFolderName(value);
    if (newFolderError) setNewFolderError(value.trim() ? newFolderErrorOf(value) : null);
  }

  function commitNewFolder() {
    const trimmed = newFolderName.trim();
    if (!trimmed) { cancelNewFolder(); return; }
    const message = newFolderErrorOf(trimmed);
    if (message) { setNewFolderError(message); return; }
    setNewFolderError(null);
    onCommitNewFolder(trimmed);
  }

  function cancelNewFolder() {
    setNewFolderError(null);
    onCancelNewFolder();
  }

  // System roots get "New subfolder" only; user folders also get rename, and —
  // for admins only (canDelete) — delete. "New folder" is hidden once the node
  // sits at the deepest allowed level — level 10 may hold files only, so no
  // subfolder can be created there.
  const ctxNodeIsSystem = ctxMenu ? !!folders.find((f) => f.id === ctxMenu.nodeId)?.isSystem : false;
  const ctxCanAddSubfolder = ctxMenu ? canCreateSubfolder(folders, ctxMenu.nodeId) : false;
  const ctxItems = ctxMenu
    ? [
        ...(ctxCanAddSubfolder && onStartNewFolder ? [
          { label: t('documents.contextMenu.newFolder'), onClick: () => handleNewSubfolder(ctxMenu.nodeId) },
        ] : []),
        ...(ctxNodeIsSystem ? [] : [
          ...(onRenameFolder ? [
            { label: t('documents.contextMenu.rename'), onClick: () => startRename(ctxMenu.nodeId) },
          ] : []),
          ...(canDelete ? [
            { label: t('documents.contextMenu.delete'), onClick: () => onDeleteFolder(ctxMenu.nodeId), danger: true },
          ] : []),
        ]),
      ]
    : [];

  return (
    <FolderNavContext.Provider value={{
      selectedId,
      expanded,
      renamingId,
      renameValue,
      renameError,
      invalidDropId,
      newFolder,
      newFolderName,
      newFolderError,
      onSelect,
      onToggle: toggleExpand,
      onContextMenu: openNodeMenu,
      onRenameChange: changeRename,
      onRenameCommit: commitRename,
      onRenameCancel: cancelRename,
      onNewFolderChange: changeNewFolder,
      onNewFolderCommit: commitNewFolder,
      onNewFolderCancel: cancelNewFolder,
    }}>
      <ScrollArea className={styles.sidebar}>
        <aside className={styles.sidebarBody} onContextMenu={openBackgroundMenu} data-tour="documents-folders">
          <div className={styles.sectionHeader}>{t('documents.projectFiles')}</div>
          <nav className={styles.tree} aria-label={t('documents.foldersAria')}>
            {isPending ? (
              <div className={styles.skeletonList} aria-hidden="true">
                {SKELETON_ROWS.map((row, i) => (
                  <div
                    key={i}
                    className={styles.skeletonRow}
                    style={{ paddingLeft: `calc(var(--s-3) + ${row.indent * 16}px)` }}
                  >
                    <div className={styles.skeletonIcon} />
                    <div className={styles.skeletonLabel} style={{ width: row.width }} />
                  </div>
                ))}
              </div>
            ) : !isUnavailable ? (
              <TreeNode node={tree} depth={0} />
            ) : null}
          </nav>

          {ctxMenu && (
            <ContextMenu
              x={ctxMenu.x}
              y={ctxMenu.y}
              items={ctxItems}
              onClose={() => setCtxMenu(null)}
            />
          )}
        </aside>
      </ScrollArea>
    </FolderNavContext.Provider>
  );
}
